import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {Modal} from 'react-bootstrap'
import useItem from '../../../../api/hooks/useItem'
import PageBuilder from '../../../../pages/PageBuilder'
import setModalIndex from '../../../helpers/setModalIndex'
import useModalSettings from '../../../helpers/useModalSettings'
import {ModalContext, useModalContext} from '../../helpers/ModalContext'
import {TComponentButton} from '../../../components/ComponentButton/_types'

type TArgs = {
    buttons: Array<TComponentButton>
    isSuccess: boolean
    response: any
    object: string
    command: string
    id?: number | string | null
    initialId?: number | string | null
    afterSubmitAction: (buttons: Array<TComponentButton>, isValuesChanged: boolean) => void
}

const normalizeIntId = (value: any): number | null => {
    if (value == null) return null
    if (typeof value === 'number' && Number.isFinite(value)) return value
    if (typeof value === 'string') {
        const s = value.trim()
        if (/^\d+$/.test(s)) return parseInt(s, 10)
    }
    return null
}

const joinPageWithId = (base: string, idValue: number) => {
    const clean = String(base || '').replace(/\/+$/g, '')
    return `${clean}/${idValue}`
}

const extractRow = (data: any) => {
    if (!data) return null
    if (Array.isArray(data)) return data[0] ?? null
    if (Array.isArray((data as any)?.data)) return (data as any).data[0] ?? null
    return (data as any)?.data ?? data
}

const extractLinkedId = (row: any, field: string | null): number | null => {
    if (!row || !field) return null
    const v = row?.[field]
    if (v && typeof v === 'object' && 'value' in v) return normalizeIntId((v as any).value)
    return normalizeIntId(v)
}

export const useAfterSubmitModal = (args: TArgs) => {
    const {buttons, isSuccess, response, object, command, id, initialId, afterSubmitAction} = args
    const outerModalContext = useModalContext()

    const submitButton: any = useMemo(() => buttons.find((b) => b.type === 'submit'), [buttons])

    const pageBase: string = submitButton?.settings?.page ?? ''
    const enabled: boolean = Boolean(submitButton?.settings?.afterSubmit && pageBase)
    const modal_size = (submitButton?.settings?.modal_size ?? 'xl') as 'sm' | 'lg' | 'xl' | 'xxl'

    const settingsContext: Record<string, any> =
        submitButton?.settings?.context && typeof submitButton.settings.context === 'object'
            ? submitButton.settings.context
            : {}

    const propertySelect: string | null = useMemo(() => {
        const v = settingsContext?.property_select
        return typeof v === 'string' && v.trim() ? v.trim() : null
    }, [settingsContext])

    const [show, setShow] = useState(false)

    const createdId = useMemo(() => {
        const fromObj = normalizeIntId(response?.data?.id)
        const fromPrim = normalizeIntId(response?.data)
        const fromArgs = normalizeIntId(id)
        const fromInit = normalizeIntId(initialId)
        return fromObj ?? fromPrim ?? fromArgs ?? fromInit ?? null
    }, [response, id, initialId])

    const shouldFetchEntity = useMemo(() => {
        if (!enabled) return false
        if (!isSuccess) return false
        if (!show) return false
        if (!propertySelect) return false
        return createdId != null
    }, [enabled, isSuccess, show, propertySelect, createdId])

    const entityRequest = useMemo(() => {
        if (!shouldFetchEntity) return null
        return {id: createdId}
    }, [shouldFetchEntity, createdId])

    const {data: entityData} = useItem<any>(object, entityRequest, shouldFetchEntity)

    const resolvedIdForPage = useMemo(() => {
        if (!enabled) return null
        if (!isSuccess) return null
        if (!createdId) return null
        if (!propertySelect) return createdId

        const row = extractRow(entityData)
        return extractLinkedId(row, propertySelect)
    }, [enabled, isSuccess, createdId, propertySelect, entityData])

    const finalPage = useMemo(() => {
        if (!pageBase) return ''
        if (resolvedIdForPage == null) return ''
        return joinPageWithId(pageBase, resolvedIdForPage)
    }, [pageBase, resolvedIdForPage])

    const shouldFetchPage = useMemo(() => {
        if (!enabled) return false
        if (!isSuccess) return false
        if (!show) return false
        return Boolean(finalPage)
    }, [enabled, isSuccess, show, finalPage])

    const pageRequest = useMemo(() => {
        if (!shouldFetchPage) return null
        return {page: finalPage}
    }, [shouldFetchPage, finalPage])

    const {data: pageData, isFetching} = useItem('pages', pageRequest, shouldFetchPage)

    const {handleEntered, handleExited} = useModalSettings(pageBase)

    const closeAndContinue = useCallback(() => {
        setShow(false)
        afterSubmitAction(buttons, true)
    }, [afterSubmitAction, buttons])

    useEffect(() => {
        if (!enabled) return
        if (!isSuccess) return
        setShow(true)
    }, [enabled, isSuccess])

    const innerInitialData = useMemo(() => {
        const rid = resolvedIdForPage ?? undefined
        return {id: rid, row_id: rid}
    }, [resolvedIdForPage])

    const modalContextValues = useMemo(() => {
        return {
            initialData: innerInitialData,
            setInitialData: () => {},
            handleResponse: () => {
                if (outerModalContext.refetchPage) outerModalContext.refetchPage()
            },
            setShow: (state: boolean) => {
                if (state === false) closeAndContinue()
            },
            refetchPage: outerModalContext.refetchPage,
            insideModal: true,
            saveInStorage: !outerModalContext.insideModal,
            settings: {close_after_submit: true},
        }
    }, [
        innerInitialData,
        outerModalContext.refetchPage,
        outerModalContext.insideModal,
        closeAndContinue,
    ])

    const modalNode = useMemo(() => {
        if (!enabled) return null
        if (!show) return null

        return (
            <Modal
                size={modal_size === 'xxl' ? undefined : modal_size}
                centered={modal_size === 'sm'}
                dialogClassName={['customModal', modal_size === 'xxl' && 'modal-xxl-layout']
                    .filter(Boolean)
                    .join(' ')}
                show={Boolean(show) && Boolean(pageData)}
                onHide={closeAndContinue}
                onEntering={setModalIndex}
                onEntered={handleEntered}
                onExited={handleExited}
                enforceFocus={false}
            >
                <Modal.Header closeButton className='modal-emptyHeader' />
                <Modal.Body className='scroll-y'>
                    <ModalContext.Provider value={modalContextValues}>
                        {pageData ? (
                            <PageBuilder
                                data={pageData}
                                isFetching={isFetching}
                                showProgressBar={false}
                            />
                        ) : null}
                    </ModalContext.Provider>
                </Modal.Body>
            </Modal>
        )
    }, [
        enabled,
        show,
        modal_size,
        pageData,
        isFetching,
        closeAndContinue,
        handleEntered,
        handleExited,
        modalContextValues,
    ])

    return {enabled, show, modalNode}
}
